import { Switch } from "../../bits/Switch/Switch";
import * as React from "react";
import { cn } from "../../../helpers";
import { useHistory, useLocation } from "react-router-dom";
import {
    AgeCategory,
    ClientStatus,
    DateFormats,
    DayOption,
    DayOptionDays,
    IAvailabilityWindow,
    IClient,
    IClinician,
    IExperienceLevelOption,
    ISearchParams,
    TimeCategory,
    TimeCategoryHours,
} from "intake_shared/src/types";
import styles from "./FindClinician.module.scss";
import { SearchInput } from "../../bits/SearchInput/SearchInput";
import { Button } from "../../bits/Button/Button";
import { FormCheckbox } from "../../bits/FormCheckbox/FormCheckbox";
import FiltersSidebar from "./FiltersSidebar/FiltersSidebar";
import { useToasts } from "react-toast-notifications";
import { AclContext } from "../../../../contexts/AclContext";
import { cloneDeep } from "lodash";
import { isClientMinor } from "../../../../utils/age";
import moment from "../../../../utils/app_moment";
import store from "../../../../store";
import FilteredClinicians from "./FilteredClinicians/FilteredClinicians";
import EmptyListState from "../../bits/EmptyListState/EmptyListState";
import { Loader } from "../../bits/Loader/Loader";
import ExpLevelSearchResult from "./ExpLevelSearchResult/ExpLevelSearchResult";
import BookClient from "./BookClient/BookClient";
import { MetaBox } from "../../bits/MetaBox/MetaBox";
import MailIcon from "../../icons/Mail";
import PhoneIcon from "../../icons/Phone";
import CalendarIcon from "../../icons/Calendar";
import HomeIcon from "../../icons/Home";
import { getStateFromAddress } from "../../../../utils/utils";
import { TextArea } from "../../bits/TextArea/TextArea";
import { InsuranceAndFeesDisplay } from "./InsuranceFeesDisplay/InsuranceFeesDisplay";
import ChevronRightIcon from "../../icons/ChevronRight";
import { AlertDialog } from "../../bits/AlertDialog/AlertDialog";
import { ClinicianServiceSelection } from "../../../../store/department";
import ClientInquiryPreferences from "../../clients/ClientInquiryPreferences/ClientInquiryPreferences";
import { isExperienceCredentialExpired } from "intake_shared/src/util";

const defaultSearchParams: ISearchParams = {
    available_days_only: true,
    first_name: "",
    age_categories: [] as AgeCategory[],
    methodology: {},
    experience_level: {},
    search_locations: [],
    search_credentials: [],
    experience_level_filter: {},
    show_only_available: true,
    available_services: [],
    priority_marked: false,
    search_departments: [],
};

type EditableClientField =
    | "email"
    | "phone_no"
    | "primary_parent"
    | "primary_parent_last_name"
    | "other_parent"
    | "other_parent_last_name"
    | "other_parent_email"
    | "other_parent_phone"
    | "no_other_parent_guardian_reason";

interface EditableClientOverrides {
    clientId: string;
    values: Partial<Record<EditableClientField, string>>;
}

interface EditingClientFieldState {
    clientId: string;
    field: EditableClientField;
    value: string;
}

const editableClientFieldLabels: Record<EditableClientField, string> = {
    email: "Client email",
    phone_no: "Client phone",
    primary_parent: "Primary parent/guardian first name",
    primary_parent_last_name: "Primary parent/guardian last name",
    other_parent: "Other parent/guardian first name",
    other_parent_last_name: "Other parent/guardian last name",
    other_parent_email: "Other parent/guardian email",
    other_parent_phone: "Other parent/guardian phone",
    no_other_parent_guardian_reason: "No other parent/guardian reason",
};

function getEditableClientValues(
    client: IClient
): Record<EditableClientField, string> {
    return {
        email: client.email ?? "",
        phone_no: client.phone_no ?? "",
        primary_parent: client.primary_parent ?? "",
        primary_parent_last_name: client.primary_parent_last_name ?? "",
        other_parent: client.other_parent ?? "",
        other_parent_last_name: client.other_parent_last_name ?? "",
        other_parent_email: client.other_parent_email ?? "",
        other_parent_phone: client.other_parent_phone ?? "",
        no_other_parent_guardian_reason:
            client.no_other_parent_guardian_reason ?? "",
    };
}

function countEnabledFilters(searchParams: ISearchParams): number {
    let filtersEnabled = 0;
    filtersEnabled = searchParams.first_name ? filtersEnabled + 1 : filtersEnabled;
    filtersEnabled = searchParams.age_categories.length
        ? filtersEnabled + 1
        : filtersEnabled;
    filtersEnabled += Object.keys(searchParams.methodology).length;
    filtersEnabled += Object.keys(searchParams.experience_level).length;
    filtersEnabled += Object.keys(searchParams.experience_level_filter).length;
    filtersEnabled += searchParams.search_locations.length;
    filtersEnabled += searchParams.search_credentials.length;
    filtersEnabled += searchParams.available_services.length;
    return filtersEnabled;
}

function filterClinicians(
    foundClinicians: IClinician[],
    timePeriodFilters: TimeCategory[],
    dayPeriodFilters: DayOption[],
    searchParams: ISearchParams,
    showOnlyWithIntakeAvailability: boolean
) {
    return foundClinicians.filter((c: IClinician) => {
        const bookedIds = new Set([
            ...(c.confirmed || []).map((b) => b.id),
            ...(c.held || []).map((b) => b.id),
        ]);
        const unbookedAvailable = (c.available || []).filter(
            (a) => !bookedIds.has(a.id)
        );

        const availableTimes: number[] = [];
        const availableDays: number[] = [];
        unbookedAvailable.forEach((a) => {
            if (!a.reservedFor) {
                const startMoment = moment(a.start);
                availableTimes.push(startMoment.hour());
                availableDays.push(startMoment.day());
            }
        });

        const noDayTimeFilterSelected =
            timePeriodFilters.length < 1 && dayPeriodFilters.length < 1;

        const hasSelectedTime =
            timePeriodFilters.length < 1 ||
            availableTimes.some((time) => {
                for (const selectedTime of timePeriodFilters) {
                    return TimeCategoryHours[selectedTime].includes(time);
                }
                return false;
            });

        const hasSelectedDay =
            dayPeriodFilters.length < 1 ||
            availableDays.some((day) => {
                for (const selectedDay of dayPeriodFilters) {
                    return DayOptionDays[selectedDay].includes(day);
                }
                return false;
            });

        const isRightExp =
            Object.keys(searchParams.experience_level_filter).length < 1
                ? true
                : Object.entries(searchParams.experience_level_filter).every(
                      (expLvl) => {
                          const [label, value] = expLvl;
                          return (
                              c.experience_levels[label] &&
                              c.experience_levels[label].level === value &&
                              !c.experience_levels[label].do_not_want &&
                              !isExperienceCredentialExpired(
                                  c.experience_levels[label]
                              )
                          );
                      }
                  );

        const hasRightAvailabilityStatus = searchParams.show_only_available
            ? unbookedAvailable.filter((a) => !Boolean(a.reservedFor)).length > 0
            : true;

        const hasPriority = searchParams.priority_marked
            ? c.marked_as_priority
            : true;

        const offersWantedServices =
            searchParams.available_services.length < 1 ||
            unbookedAvailable.some((a) => {
                return (
                    a.availableService &&
                    searchParams.available_services.includes(a.availableService)
                );
            });

        const hasVisibleIntakeAvailability = (
            c.available_for_intake_sessions || []
        ).some((slot) => {
            if (!slot.start_time) return false;
            const slotStart = moment(slot.start_time);
            if (!slotStart.isValid() || !slotStart.isAfter(moment())) {
                return false;
            }
            if (slot.booking_id || slot.client_id || slot.client_display_name) {
                return false;
            }
            return true;
        });

        const passesIntakeAvailabilityFilter = showOnlyWithIntakeAvailability
            ? hasVisibleIntakeAvailability
            : true;

        return (
            (noDayTimeFilterSelected || (hasSelectedTime && hasSelectedDay)) &&
            isRightExp &&
            hasRightAvailabilityStatus &&
            hasPriority &&
            offersWantedServices &&
            passesIntakeAvailabilityFilter
        );
    });
}

export default function FindClinician() {
    const location = useLocation();
    const clientFromRoute = ((location.state || {}) as any)?.client as
        | IClient
        | undefined;
    const selectedClient: IClient = clientFromRoute ?? ({} as IClient);
    const hasClient = Boolean(clientFromRoute?.id);
    const history = useHistory();
    const { addToast } = useToasts();
    const aclContext = React.useContext(AclContext);
    const loggedInUserEmail = aclContext?.authUser.user?.email;
    const isSuperAdmin = Boolean(aclContext?.authUser.acl?.super_admin);
    const [showLoader, setShowLoader] = React.useState(false);

    const [searchParams, setSearchParams] = React.useState<ISearchParams>(
        cloneDeep(defaultSearchParams)
    );
    const [selectedAppointmentFromCard, setSelectedAppointmentFromCard] =
        React.useState<boolean>(false);
    const [foundClinicians, setFoundClincians] = React.useState([] as IClinician[]);
    const [filteredClinicians, setFilteredClincians] = React.useState(
        [] as IClinician[]
    );
    const [showOnlyWithIntakeAvailability, setShowOnlyWithIntakeAvailability] =
        React.useState(false);
    const [clinicianToBook, setClinicianToBook] = React.useState({} as IClinician);

    const [timePeriodFilters, setTimePeriodFilters] = React.useState<TimeCategory[]>(
        []
    );
    const updateTimePeriodFilter = (timePeriod: TimeCategory) => {
        if (!timePeriodFilters.includes(timePeriod)) {
            setTimePeriodFilters([...timePeriodFilters, timePeriod]);
        } else {
            setTimePeriodFilters([
                ...timePeriodFilters.filter((t) => t !== timePeriod),
            ]);
        }
    };
    const [clinicianServiceSelections, setClinicianServiceSelections] =
        React.useState<ClinicianServiceSelection>({});
    const fetchClinicianServiceSelection = async () => {
        setClinicianServiceSelections(
            await store.fetchClinicianServiceSelection(clinicianToBook.id as string)
        );
    };

    React.useEffect(() => {
        fetchClinicianServiceSelection();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clinicianToBook.id]);

    const [dayPeriodFilters, setdayPeriodFilters] = React.useState<DayOption[]>([]);
    const updateDayPeriodFilter = (dayPeriod: DayOption) => {
        if (!dayPeriodFilters.includes(dayPeriod)) {
            setdayPeriodFilters([...dayPeriodFilters, dayPeriod]);
        } else {
            setdayPeriodFilters([
                ...dayPeriodFilters.filter((t) => t !== dayPeriod),
            ]);
        }
    };

    const [supervisors, setAllSupervisors] = React.useState([] as IClinician[]);

    const getSupervisors = async () => {
        const response = await store.fetchAllSupervisors();
        setAllSupervisors(response);
    };

    const [allExperienceLevelOptions, setAllExperienceLevelOptions] = React.useState(
        [] as IExperienceLevelOption[]
    );

    React.useEffect(() => {
        const fetchExperienceLevels = async () => {
            try {
                const levels = await store.getAllExperienceLevels();
                setAllExperienceLevelOptions(levels);
            } catch (ex) {
                console.error(ex);
            }
        };
        fetchExperienceLevels();
    }, []);

    React.useEffect(() => {
        getSupervisors();
    }, []);

    React.useEffect(() => {
        const filteredClinicians = filterClinicians(
            foundClinicians,
            timePeriodFilters,
            dayPeriodFilters,
            searchParams,
            showOnlyWithIntakeAvailability
        );

        const updatedClinicians = filteredClinicians.map((clinician) => {
            const supervisor = supervisors.find(
                (supervisor) => supervisor.id === clinician.supervisor_id
            );

            const secondarySupervisors = supervisors.filter((s) =>
                clinician.secondary_supervisor_ids?.some(
                    (ss) => ss.supervisor_id === s.id
                )
            );

            return {
                ...clinician,
                supervisorFirstName: supervisor ? supervisor.firstname : "",
                supervisorLastName: supervisor ? supervisor.lastname : "",
                secondary_supervisor_ids: secondarySupervisors.map((s) => {
                    const existingPair = clinician.secondary_supervisor_ids?.find(
                        (ss) => ss.supervisor_id === s.id
                    );

                    return {
                        practice_id: existingPair?.practice_id || "",
                        supervisor_id: s.email as string,
                        supervisor_first_name: s.firstname,
                        supervisor_last_name: s.lastname,
                    };
                }),
            };
        });

        setFilteredClincians(updatedClinicians);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        timePeriodFilters,
        dayPeriodFilters,
        foundClinicians,
        searchParams,
        showOnlyWithIntakeAvailability,
    ]);

    const [availabilityToShow, setAvailabilityToShow] = React.useState(
        {} as IAvailabilityWindow
    );

    const showAvailabilityOneLiner = (availability: IAvailabilityWindow) => {
        const startMoment = moment(availability.start);
        return (
            <p>
                On <strong>{startMoment.format(DateFormats.DAY_HOUR_FORMAT)}</strong>{" "}
                recurring <strong>{availabilityToShow.recurrenceRule}</strong>{" "}
                <strong>
                    {startMoment.isAfter(moment(), "day")
                        ? `starting ${startMoment.fromNow()} - ${startMoment.format(
                              DateFormats.DATE_FORMAT
                          )}`
                        : ""}
                </strong>{" "}
                at{" "}
                <strong>
                    {availabilityToShow.location} {availabilityToShow.office}
                </strong>
            </p>
        );
    };

    const [selectedApptToHoldId, setSelectedApptToHoldId] = React.useState("");

    const [clientConcerns, setClientConcerns] = React.useState("");
    // Results layout is a per-person preference, remembered in this browser
    const [resultsLayout, setResultsLayout] = React.useState<"grid" | "list">(() => {
        try {
            return localStorage.getItem("find_clinician_layout") === "list"
                ? "list"
                : "grid";
        } catch {
            return "grid";
        }
    });
    const changeResultsLayout = (layout: "grid" | "list") => {
        setResultsLayout(layout);
        try {
            localStorage.setItem("find_clinician_layout", layout);
        } catch {
            // Storage can be unavailable (private mode); the choice just won't persist
        }
    };
    const [clientCallNotes, setClientCallNotes] = React.useState(
        selectedClient[ClientStatus.APPT_SETTING]?.call_notes || ""
    );
    const [editingClientField, setEditingClientField] =
        React.useState<EditingClientFieldState | null>(null);
    const [editableClientOverrides, setEditableClientOverrides] =
        React.useState<EditableClientOverrides>({
            clientId: selectedClient.id,
            values: {},
        });
    const editableClientValues: Record<EditableClientField, string> = {
        ...getEditableClientValues(selectedClient),
        ...(editableClientOverrides.clientId === selectedClient.id
            ? editableClientOverrides.values
            : {}),
    };
    const selectedClientWithEditableValues: IClient = {
        ...selectedClient,
        ...editableClientValues,
    };
    const activeEditingClientField =
        editingClientField?.clientId === selectedClient.id
            ? editingClientField
            : null;

    const startEditingClientField = (field: EditableClientField) => {
        setEditableClientOverrides((currentOverrides) =>
            currentOverrides.clientId === selectedClient.id
                ? currentOverrides
                : {
                      clientId: selectedClient.id,
                      values: {},
                  }
        );
        setEditingClientField({
            clientId: selectedClient.id,
            field,
            value: editableClientValues[field],
        });
    };

    const cancelEditingClientField = () => {
        setEditingClientField(null);
    };

    const updateEditingClientFieldValue = (value: string) => {
        setEditingClientField((currentField) =>
            currentField?.clientId === selectedClient.id
                ? {
                      ...currentField,
                      value,
                  }
                : currentField
        );
    };

    const handleSaveClientField = async (field: EditableClientField) => {
        const clientId = selectedClient.id;
        const trimmedValue =
            activeEditingClientField?.field === field
                ? activeEditingClientField.value.trim()
                : "";
        const updatedValue =
            field === "email" ? trimmedValue.toLowerCase() : trimmedValue;
        if (!updatedValue) {
            addToast(`${editableClientFieldLabels[field]} is required`, {
                appearance: "error",
            });
            return;
        }

        try {
            await store.updateClientData({
                client_id: clientId,
                changes: {
                    [field]: updatedValue,
                },
                last_updated_by: loggedInUserEmail as string,
            });
            setEditableClientOverrides((currentOverrides) => {
                if (currentOverrides.clientId !== clientId) {
                    return currentOverrides;
                }
                return {
                    ...currentOverrides,
                    values: {
                        ...currentOverrides.values,
                        [field]: updatedValue,
                    },
                };
            });
            addToast(`${editableClientFieldLabels[field]} updated successfully`, {
                appearance: "success",
            });
            setEditingClientField((currentField) =>
                currentField?.clientId === clientId && currentField.field === field
                    ? null
                    : currentField
            );
        } catch {
            addToast(`Unable to update ${editableClientFieldLabels[field]}`, {
                appearance: "error",
            });
        }
    };

    const renderEditableClientMetaBox = (
        field: EditableClientField,
        icon?: React.ReactNode
    ) => {
        const value = editableClientValues[field];
        return (
            <MetaBox
                icon={icon}
                label={
                    field === "email" || field === "phone_no"
                        ? undefined
                        : editableClientFieldLabels[field]
                }
                value={value.trim() ? value : "--"}
                isEditing={activeEditingClientField?.field === field}
                editValue={activeEditingClientField?.value ?? value}
                onEdit={() => startEditingClientField(field)}
                onSave={() => handleSaveClientField(field)}
                onCancel={cancelEditingClientField}
                onValueChange={updateEditingClientFieldValue}
            />
        );
    };

    const runClinicianSearch = async () => {
        setShowLoader(true);
        try {
            const clinicians = await store.searchClincians(searchParams);
            setFoundClincians(clinicians);

            let message = `${clinicians.length} clinicians found matching the criteria.`;
            addToast(message, { appearance: "success" });
        } catch (error) {
            addToast("Unable to search clinicians. Please try again.", {
                appearance: "error",
            });
        } finally {
            setShowLoader(false);
        }
    };

    const handleClinicianSearchSubmit = async (
        e: React.FormEvent<HTMLFormElement>
    ) => {
        e.preventDefault();
        await runClinicianSearch();
    };

    const filtersCount = countEnabledFilters(searchParams);
    const isMinor = isClientMinor(selectedClient?.date_of_birth);

    const [searchInputValue, setSearchInputValue] = React.useState("");

    return (
        <>
            <Loader show={showLoader} />
            <AlertDialog
                open={Boolean(availabilityToShow.start)}
                description={showAvailabilityOneLiner(availabilityToShow)}
                onClose={() => {
                    setAvailabilityToShow({} as IAvailabilityWindow);
                }}
                title={`${moment(availabilityToShow.start).format(
                    DateFormats.DAY_HOUR_FORMAT
                )} Availability`}
                hideFooter
            />
            <div
                className={cn("v2-page", styles.findClinicianWrapper, {
                    [styles.withClientInfo]: hasClient,
                })}
            >
                <FiltersSidebar
                    searchParams={searchParams}
                    setSearchParams={setSearchParams}
                    filtersCount={filtersCount}
                    defaultSearchParams={defaultSearchParams}
                    setTimePeriodFilters={setTimePeriodFilters}
                    setdayPeriodFilters={setdayPeriodFilters}
                    setSearchInputValue={setSearchInputValue}
                />

                <div className={styles.cliniciansWrapper}>
                    <div className={styles.cliniciansHeader}>
                        <div className={styles.topFilters}>
                            <div className={styles.topFiltersGrid}>
                                <form
                                    className={styles.clinicianSearchForm}
                                    onSubmit={handleClinicianSearchSubmit}
                                >
                                    <SearchInput
                                        placeholder="Search by first name"
                                        value={searchInputValue}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setSearchInputValue(value);
                                            setSearchParams({
                                                ...searchParams,
                                                first_name: value,
                                            });
                                        }}
                                    />

                                    <Button variant="primary" type="submit">
                                        Find Clinicians
                                    </Button>
                                </form>
                            </div>

                            <div className={styles.topFiltersGrid}>
                                <FormCheckbox
                                    label="Show only available"
                                    checked={searchParams.show_only_available}
                                    onChange={(e) => {
                                        setSearchParams({
                                            ...searchParams,
                                            show_only_available: e.target.checked,
                                        });
                                    }}
                                    noBorder
                                    labelClass={styles.borderlessLabel}
                                />
                                <FormCheckbox
                                    label="Show priority clinicians"
                                    checked={searchParams.priority_marked}
                                    onChange={(e) => {
                                        setSearchParams({
                                            ...searchParams,
                                            priority_marked: e.target.checked,
                                        });
                                    }}
                                    noBorder
                                    labelClass={styles.borderlessLabel}
                                />
                                <FormCheckbox
                                    label="Has intake sessions"
                                    checked={showOnlyWithIntakeAvailability}
                                    onChange={(e) => {
                                        setShowOnlyWithIntakeAvailability(
                                            e.target.checked
                                        );
                                    }}
                                    noBorder
                                    labelClass={styles.borderlessLabel}
                                />
                            </div>

                            <div className={styles.topFiltersGrid}>
                                <div className={styles.timeFilters}>
                                    <FormCheckbox
                                        label="Early morning"
                                        checked={timePeriodFilters.includes(
                                            TimeCategory.EARLY_MORNING
                                        )}
                                        onChange={() =>
                                            updateTimePeriodFilter(
                                                TimeCategory.EARLY_MORNING
                                            )
                                        }
                                        variant="small"
                                        wrapperClass={styles.timeCheckbox}
                                        labelClass={styles.timeCheckboxLabel}
                                        inputClass={styles.timeCheckboxInput}
                                    />
                                    <FormCheckbox
                                        label="Morning"
                                        checked={timePeriodFilters.includes(
                                            TimeCategory.MORNING
                                        )}
                                        onChange={() =>
                                            updateTimePeriodFilter(
                                                TimeCategory.MORNING
                                            )
                                        }
                                        variant="small"
                                        wrapperClass={styles.timeCheckbox}
                                        labelClass={styles.timeCheckboxLabel}
                                        inputClass={styles.timeCheckboxInput}
                                    />
                                    <FormCheckbox
                                        label="Afternoon"
                                        checked={timePeriodFilters.includes(
                                            TimeCategory.AFTERNOON
                                        )}
                                        onChange={() =>
                                            updateTimePeriodFilter(
                                                TimeCategory.AFTERNOON
                                            )
                                        }
                                        variant="small"
                                        wrapperClass={styles.timeCheckbox}
                                        labelClass={styles.timeCheckboxLabel}
                                        inputClass={styles.timeCheckboxInput}
                                    />
                                    <FormCheckbox
                                        label="Evening"
                                        checked={timePeriodFilters.includes(
                                            TimeCategory.EVENING
                                        )}
                                        onChange={() =>
                                            updateTimePeriodFilter(
                                                TimeCategory.EVENING
                                            )
                                        }
                                        wrapperClass={styles.timeCheckbox}
                                        labelClass={styles.timeCheckboxLabel}
                                        inputClass={styles.timeCheckboxInput}
                                        variant="small"
                                    />
                                    <FormCheckbox
                                        label="Lunch Time"
                                        checked={timePeriodFilters.includes(
                                            TimeCategory.LUNCH_TIME
                                        )}
                                        onChange={() =>
                                            updateTimePeriodFilter(
                                                TimeCategory.LUNCH_TIME
                                            )
                                        }
                                        wrapperClass={styles.timeCheckbox}
                                        labelClass={styles.timeCheckboxLabel}
                                        inputClass={styles.timeCheckboxInput}
                                        variant="small"
                                    />
                                    <Button
                                        onClick={() => {
                                            setTimePeriodFilters([]);
                                            setdayPeriodFilters([]);
                                        }}
                                        variant="secondary"
                                        className={styles.uncheckAllButton}
                                    >
                                        Clear days & times
                                    </Button>
                                </div>

                                <div className={styles.dayFilters}>
                                    <FormCheckbox
                                        label="Sun"
                                        checked={dayPeriodFilters.includes(
                                            DayOption.SUNDAY
                                        )}
                                        onChange={() =>
                                            updateDayPeriodFilter(DayOption.SUNDAY)
                                        }
                                        wrapperClass={styles.sundayCheckbox}
                                        labelClass={styles.sundayCheckboxLabel}
                                        inputClass={styles.timeCheckboxInput}
                                        variant="small"
                                    />
                                    <FormCheckbox
                                        label="Mon"
                                        checked={dayPeriodFilters.includes(
                                            DayOption.MONDAY
                                        )}
                                        onChange={() =>
                                            updateDayPeriodFilter(DayOption.MONDAY)
                                        }
                                        wrapperClass={styles.mondayCheckbox}
                                        labelClass={styles.mondayCheckboxLabel}
                                        inputClass={styles.timeCheckboxInput}
                                        variant="small"
                                    />
                                    <FormCheckbox
                                        label="Tue"
                                        checked={dayPeriodFilters.includes(
                                            DayOption.TUESDAY
                                        )}
                                        onChange={() =>
                                            updateDayPeriodFilter(DayOption.TUESDAY)
                                        }
                                        wrapperClass={styles.tuesdayCheckbox}
                                        labelClass={styles.tuesdayCheckboxLabel}
                                        inputClass={styles.timeCheckboxInput}
                                        variant="small"
                                    />
                                    <FormCheckbox
                                        label="Wed"
                                        checked={dayPeriodFilters.includes(
                                            DayOption.WEDNESDAY
                                        )}
                                        onChange={() =>
                                            updateDayPeriodFilter(
                                                DayOption.WEDNESDAY
                                            )
                                        }
                                        wrapperClass={styles.wednesdayCheckbox}
                                        labelClass={styles.wednesdayCheckboxLabel}
                                        inputClass={styles.timeCheckboxInput}
                                        variant="small"
                                    />
                                    <FormCheckbox
                                        label="Thu"
                                        checked={dayPeriodFilters.includes(
                                            DayOption.THURSDAY
                                        )}
                                        onChange={() =>
                                            updateDayPeriodFilter(DayOption.THURSDAY)
                                        }
                                        wrapperClass={styles.thursdayCheckbox}
                                        labelClass={styles.thursdayCheckboxLabel}
                                        inputClass={styles.timeCheckboxInput}
                                        variant="small"
                                    />
                                    <FormCheckbox
                                        label="Fri"
                                        checked={dayPeriodFilters.includes(
                                            DayOption.FRIDAY
                                        )}
                                        onChange={() =>
                                            updateDayPeriodFilter(DayOption.FRIDAY)
                                        }
                                        wrapperClass={styles.fridayCheckbox}
                                        labelClass={styles.fridayCheckboxLabel}
                                        inputClass={styles.timeCheckboxInput}
                                        variant="small"
                                    />
                                    <FormCheckbox
                                        label="Sat"
                                        checked={dayPeriodFilters.includes(
                                            DayOption.SATURDAY
                                        )}
                                        onChange={() =>
                                            updateDayPeriodFilter(DayOption.SATURDAY)
                                        }
                                        wrapperClass={styles.saturdayCheckbox}
                                        labelClass={styles.saturdayCheckboxLabel}
                                        inputClass={styles.timeCheckboxInput}
                                        variant="small"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        className={cn(styles.findClinicianContent, {
                            [styles.withClientInfo]: hasClient,
                        })}
                    >
                        <div className={styles.cliniciansList}>
                            <div className={styles.resultsCount}>
                                <span className={styles.resultsCountText}>
                                    Found {filteredClinicians.length} clinician(s)
                                    matching the search criteria
                                </span>
                                {isSuperAdmin &&
                                    filteredClinicians.length > 0 &&
                                    Object.entries(
                                        searchParams.experience_level
                                    ).some(([, v]) => v) && (
                                        <Button
                                            onClick={() => {
                                                const filteredExpKeys =
                                                    Object.entries(
                                                        searchParams.experience_level
                                                    )
                                                        .filter(([, v]) => v)
                                                        .map(([k]) => k);
                                                const sortedExpKeys =
                                                    allExperienceLevelOptions
                                                        .filter(
                                                            (opt) =>
                                                                opt.category !==
                                                                    "Provider Style" &&
                                                                opt.value !==
                                                                    "Young Adults (18-25)" &&
                                                                opt.value !==
                                                                    "EMDR" &&
                                                                (filteredExpKeys.length ===
                                                                    0 ||
                                                                    filteredExpKeys.includes(
                                                                        opt.value
                                                                    ))
                                                        )
                                                        .sort((a, b) => {
                                                            if (
                                                                a.category ===
                                                                    "Licensing" &&
                                                                b.category !==
                                                                    "Licensing"
                                                            )
                                                                return 1;
                                                            if (
                                                                b.category ===
                                                                    "Licensing" &&
                                                                a.category !==
                                                                    "Licensing"
                                                            )
                                                                return -1;
                                                            return a.value.localeCompare(
                                                                b.value
                                                            );
                                                        })
                                                        .map((opt) => opt.value);

                                                const headers = [
                                                    "Name",
                                                    "Credential",
                                                    ...sortedExpKeys,
                                                ];
                                                const rows = [...filteredClinicians]
                                                    .sort((a, b) =>
                                                        a.firstname.localeCompare(
                                                            b.firstname
                                                        )
                                                    )
                                                    .map((c) => {
                                                        const expCols =
                                                            sortedExpKeys.map(
                                                                (k) => {
                                                                    const lvl =
                                                                        c
                                                                            .experience_levels[
                                                                            k
                                                                        ];
                                                                    if (!lvl)
                                                                        return "";
                                                                    if (
                                                                        lvl.do_not_want
                                                                    )
                                                                        return "DNB";
                                                                    return (
                                                                        lvl.level ||
                                                                        ""
                                                                    );
                                                                }
                                                            );
                                                        return [
                                                            c.fullname,
                                                            c.credential,
                                                            ...expCols,
                                                        ];
                                                    });

                                                const sanitize = (
                                                    value: unknown
                                                ) => {
                                                    const s = String(value ?? "");
                                                    return /^[=+\-@]/.test(
                                                        s.trimStart()
                                                    )
                                                        ? `'${s}`
                                                        : s;
                                                };
                                                const csvContent = [headers, ...rows]
                                                    .map((row) =>
                                                        row
                                                            .map(
                                                                (cell) =>
                                                                    `"${sanitize(
                                                                        cell
                                                                    ).replace(
                                                                        /"/g,
                                                                        '""'
                                                                    )}"`
                                                            )
                                                            .join(",")
                                                    )
                                                    .join("\n");

                                                const blob = new Blob([csvContent], {
                                                    type: "text/csv;charset=utf-8;",
                                                });
                                                const url =
                                                    URL.createObjectURL(blob);
                                                const link =
                                                    document.createElement("a");
                                                link.setAttribute("href", url);
                                                link.setAttribute(
                                                    "download",
                                                    `clinician-experience-levels-${moment().format(
                                                        "YYYY-MM-DD"
                                                    )}.csv`
                                                );
                                                document.body.appendChild(link);
                                                link.click();
                                                document.body.removeChild(link);
                                                URL.revokeObjectURL(url);
                                            }}
                                        >
                                            Export
                                        </Button>
                                    )}
                                {filteredClinicians.length > 0 && (
                                    <Switch
                                        switchOptions={[
                                            {
                                                title: "Cards",
                                                key: "grid",
                                                onClick: () =>
                                                    changeResultsLayout("grid"),
                                            },
                                            {
                                                title: "List",
                                                key: "list",
                                                onClick: () =>
                                                    changeResultsLayout("list"),
                                            },
                                        ]}
                                        activeSwitch={resultsLayout}
                                    />
                                )}
                            </div>
                            <ExpLevelSearchResult
                                clinicians={filteredClinicians}
                                searchParams={searchParams}
                                setSearchParams={setSearchParams}
                            />

                            {filteredClinicians.length > 0 ? (
                                <FilteredClinicians
                                    filteredClinicians={filteredClinicians}
                                    searchParams={searchParams}
                                    setClinicianToBook={setClinicianToBook}
                                    setSelectedApptToHoldId={setSelectedApptToHoldId}
                                    setAvailabilityToShow={setAvailabilityToShow}
                                    runClinicianSearch={runClinicianSearch}
                                    setSelectedAppointmentFromCard={
                                        setSelectedAppointmentFromCard
                                    }
                                    allExperienceLevelOptions={
                                        allExperienceLevelOptions
                                    }
                                    withClientInfo={hasClient}
                                    layout={resultsLayout}
                                />
                            ) : (
                                <EmptyListState
                                    emptyMessage="No Clinicians Found"
                                    stateHelperText="Clinicians matching your search criteria will appear here once you start searching."
                                />
                            )}
                        </div>
                        {hasClient && (
                            <div className={styles.clientInfoPanel}>
                                <div className={styles.clientInfoHeader}>
                                    <p className={styles.clientName}>
                                        {selectedClient.first_name}{" "}
                                        {selectedClient.last_name}
                                    </p>
                                    <Button
                                        variant="normal"
                                        className={styles.backButton}
                                        onClick={async () => {
                                            if (
                                                clientCallNotes !==
                                                selectedClient[
                                                    ClientStatus.APPT_SETTING
                                                ]?.call_notes
                                            ) {
                                                const copyClient = {
                                                    ...selectedClient,
                                                };
                                                copyClient[
                                                    ClientStatus.APPT_SETTING
                                                ] = {
                                                    ...(copyClient[
                                                        ClientStatus.APPT_SETTING
                                                    ] || {}),
                                                    call_notes: clientCallNotes,
                                                };
                                                await store.saveClientData(
                                                    copyClient,
                                                    loggedInUserEmail!
                                                );
                                            }
                                            history.goBack();
                                        }}
                                    >
                                        <ChevronRightIcon stroke="var(--color-text-strong)" />
                                    </Button>
                                </div>

                                <div className={styles.clientProfileInfo}>
                                    {isMinor && (
                                        <div className={styles.clientProfileInfo}>
                                            {renderEditableClientMetaBox(
                                                "primary_parent"
                                            )}
                                            {renderEditableClientMetaBox(
                                                "primary_parent_last_name"
                                            )}
                                            {selectedClient.has_no_other_parent_guardian ? (
                                                renderEditableClientMetaBox(
                                                    "no_other_parent_guardian_reason"
                                                )
                                            ) : (
                                                <>
                                                    {renderEditableClientMetaBox(
                                                        "other_parent"
                                                    )}
                                                    {renderEditableClientMetaBox(
                                                        "other_parent_last_name"
                                                    )}
                                                    {renderEditableClientMetaBox(
                                                        "other_parent_email"
                                                    )}
                                                    {renderEditableClientMetaBox(
                                                        "other_parent_phone"
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}
                                    {selectedClient.preferred_name && (
                                        <MetaBox
                                            label="Preferred Name"
                                            value={selectedClient.preferred_name}
                                        />
                                    )}
                                    {renderEditableClientMetaBox(
                                        "email",
                                        <MailIcon />
                                    )}
                                    {renderEditableClientMetaBox(
                                        "phone_no",
                                        <PhoneIcon />
                                    )}
                                    {selectedClient.date_of_birth && (
                                        <MetaBox
                                            icon={<CalendarIcon />}
                                            value={`${moment().diff(
                                                moment(
                                                    selectedClient?.date_of_birth
                                                ),
                                                "years"
                                            )} Years Old (${moment(
                                                selectedClient?.date_of_birth
                                            ).format(
                                                DateFormats.DAY_MONTH_YEAR_FORMAT
                                            )})`}
                                        />
                                    )}
                                    <MetaBox
                                        icon={<HomeIcon />}
                                        value={
                                            selectedClient.state ||
                                            getStateFromAddress(
                                                selectedClient.address
                                            ) ||
                                            "--"
                                        }
                                    />
                                    <MetaBox
                                        label="Insurance"
                                        value={
                                            selectedClient.insurance_provider || "--"
                                        }
                                    />
                                </div>

                                <div className={styles.clientNotes}>
                                    <TextArea
                                        label="Call Notes"
                                        value={clientCallNotes}
                                        onChange={(e) => {
                                            setClientCallNotes(e.target.value);
                                        }}
                                        className={styles.notesTextarea}
                                    />
                                    {selectedClient.notes && (
                                        <div className={styles.notes}>
                                            <strong>Notes:</strong>
                                            <p>{selectedClient.notes}</p>
                                        </div>
                                    )}
                                </div>

                                <ClientInquiryPreferences
                                    client={selectedClient}
                                    variant="sidebar"
                                    title="Matching preferences"
                                />

                                <InsuranceAndFeesDisplay client={selectedClient} />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <BookClient
                setClinicianToBook={setClinicianToBook}
                clinicianToBook={clinicianToBook}
                selectedClientToHold={selectedClientWithEditableValues}
                clientConcerns={clientConcerns}
                setClientConcerns={setClientConcerns}
                selectedApptToHoldId={selectedApptToHoldId}
                setSelectedApptToHoldId={setSelectedApptToHoldId}
                clientCallNotes={clientCallNotes}
                selectedAppointmentFromCard={selectedAppointmentFromCard}
                setSelectedAppointmentFromCard={setSelectedAppointmentFromCard}
                clinicianServiceSelections={clinicianServiceSelections}
            />
        </>
    );
}
